/* ============================================================================
   CY LOGO SWEEP
   Gives the header wordmark the footer's hover: each letterform turns brand red
   on a stagger, rippling across the mark, colour only - nothing moves. On phones,
   where :hover never fires, it also plays that sweep to itself every 3s (see
   selfPlay) - the footer wordmark's cadence, at the owner's request.

   The header renders the logo as <img src="…cybeart-logo-white.svg">, and an
   <img> has no addressable letterforms, so the file is fetched once and inlined
   as SVG. If that fetch fails for any reason the original <img> is left exactly
   as it was - the logo must never be at risk from an effect.

   The stagger index is assigned here rather than with :nth-child because the
   CorelDRAW export lists the paths out of visual order; sorting on the real
   getBBox().x is what makes the ripple travel across the mark instead of
   jumping around it. Same six sweep patterns as the footer, re-picked on every
   hover, so it never runs the same way twice.
   ========================================================================== */
(function () {
  'use strict';

  var CACHE = null;   // one fetch serves the default and sticky logos

  function orderedShapes(svg) {
    return Array.prototype.slice.call(svg.querySelectorAll('path, polygon'))
      .map(function (el) { return { el: el, x: el.getBBox().x }; })
      .sort(function (a, b) { return a.x - b.x; })
      .map(function (o) { return o.el; });
  }

  function wireSweep(svg, hoverTarget) {
    var ordered;
    try { ordered = orderedShapes(svg); }
    catch (e) { return; }            // getBBox throws before layout - harmless
    var n = ordered.length;
    if (!n) return;

    var patterns = [
      function (i) { return i; },                                       // left to right
      function (i) { return n - 1 - i; },                                // right to left
      function (i) { return Math.abs(i - (n - 1) / 2) | 0; },            // centre outwards
      function (i) { return (n / 2 - Math.abs(i - (n - 1) / 2)) | 0; },  // edges inwards
      function (i) { return i % 2 ? n - i : i; },                        // interleaved
      null                                                               // shuffle
    ];

    function apply() {
      var p = patterns[Math.floor(Math.random() * patterns.length)];
      if (p) {
        ordered.forEach(function (el, i) { el.style.setProperty('--cy-i', p(i)); });
      } else {
        var idx = ordered.map(function (_, i) { return i; });
        for (var i = idx.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var t = idx[i]; idx[i] = idx[j]; idx[j] = t;
        }
        ordered.forEach(function (el, i) { el.style.setProperty('--cy-i', idx[i]); });
      }
    }

    apply();
    hoverTarget.addEventListener('mouseenter', apply);
    hoverTarget.addEventListener('focusin', apply);

    /* The default and sticky logos are TWO svgs inside ONE <a>, and the swept
       state is a class on that anchor - so the self-play timer has to live on
       the anchor and drive every mark wired to it. Two timers toggling one
       class would interleave their holds and strobe it. */
    var reg = hoverTarget.cySweepReg;
    if (!reg) { reg = hoverTarget.cySweepReg = { apply: [], started: false }; }
    reg.apply.push(apply);
    if (reg.started) return;
    reg.started = true;
    selfPlay(hoverTarget, reg, n);
  }

  /* ---- self-playing sweep, phones only --------------------------------------
     There is no hover on a touch screen, so the header mark would never do its
     one trick there. It now replays the sweep every 3s - the footer wordmark's
     cadence, chosen by the owner on 2026-09-01 over a gentler on-arrival option
     - picking a fresh one of the 6 stagger patterns each time, so it never
     repeats itself.

     Gated off on desktop (hover covers it), under prefers-reduced-motion, while
     the header is scrolled out of view, and while the tab is backgrounded. That
     last pair matters more here than in the footer: this header is sticky, so
     without the gates an unattended interval would repaint an 11-shape SVG for
     the entire session on every page. A real tap stops the timer, plays once and
     resyncs, so finger and timer never fight over the same class. */
  function selfPlay(link, reg, n) {
    var mqMobile = window.matchMedia('(max-width: 767px)');
    var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    /* the longest stagger (n-1 steps) plus the fill transition, so the class is
       held until the last letterform has finished arriving at red */
    var HOLD = 300 + (n - 1) * 32;
    var EVERY = 3000;
    var FIRST = 1200;   /* one beat after it appears, then the steady cycle */
    var MAX_PLAYS = Infinity, plays = 0;   /* loops again (owner 2026-10-01: "after scroll sometimes stopped"; the Phase 3 cap of 2 removed) */
    var timer = null, first = null, hold = null;
    var onScreen = !window.IntersectionObserver;

    function play() {
      if (++plays >= MAX_PLAYS) { clearInterval(timer); timer = null; }
      for (var i = 0; i < reg.apply.length; i++) { reg.apply[i](); }
      link.classList.add('is-sweep');
      clearTimeout(hold);
      hold = setTimeout(function () { link.classList.remove('is-sweep'); }, HOLD);
    }
    function start() {
      if (plays >= MAX_PLAYS) return;
      if (timer || !onScreen || !mqMobile.matches || mqReduce.matches) return;
      if (document.hidden) return;
      first = setTimeout(play, FIRST);
      timer = setInterval(play, EVERY);
    }
    function stop() {
      clearInterval(timer); clearTimeout(first); clearTimeout(hold);
      timer = first = hold = null;
      link.classList.remove('is-sweep');
    }
    function sync() { if (timer) stop(); start(); }

    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        if (onScreen) start(); else stop();
      }, { threshold: .35 }).observe(link);
    } else {
      start();
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });
    /* rotating the phone can cross the breakpoint in either direction */
    var onMQ = function () { stop(); start(); };
    if (mqMobile.addEventListener) {
      mqMobile.addEventListener('change', onMQ);
      mqReduce.addEventListener('change', onMQ);
    } else if (mqMobile.addListener) {
      mqMobile.addListener(onMQ);
      mqReduce.addListener(onMQ);
    }
    link.addEventListener('touchstart', function () {
      stop(); play(); setTimeout(sync, HOLD + 60);
    }, { passive: true });
  }

  function inline(img) {
    var src = (img.currentSrc || img.getAttribute('src') || '').split('?')[0];
    if (!src || src.slice(-4).toLowerCase() !== '.svg') return;

    var load = CACHE || (CACHE = fetch(src).then(function (r) {
      if (!r.ok) throw new Error('logo fetch ' + r.status);
      return r.text();
    }));

    load.then(function (markup) {
      var holder = document.createElement('div');
      holder.innerHTML = markup;
      var svg = holder.querySelector('svg');
      if (!svg) return;

      svg.setAttribute('class', 'cy-logo');
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', img.getAttribute('alt') || 'Cybeart');
      svg.setAttribute('focusable', 'false');
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      /* the file paints itself white/red; currentColor lets CSS drive the sweep */
      svg.querySelectorAll('path, polygon').forEach(function (s) {
        s.setAttribute('fill', 'currentColor');
        s.removeAttribute('class');
      });
      /* the export ships a <style> block with .fil0{fill:...} that would win */
      svg.querySelectorAll('style, defs').forEach(function (n) { n.remove(); });

      /* Sizing is applied here, not in the stylesheet: the theme sets the logo
         width with !important on the <img>, and a stylesheet rule on the SVG
         lost to it (asked for 230px, measured 129px). The widths come from the
         image's own sizes attribute - "(min-width: 768px) 230px, 170px" - so
         the mark keeps whatever logo_width the theme settings define. */
      var sizes = img.getAttribute('sizes') || '';
      var wide = (sizes.match(/\)\s*(\d+)px/) || [])[1] || img.getAttribute('width') || 230;
      var narrow = (sizes.match(/,\s*(\d+)px\s*$/) || [])[1] || wide;
      var mq = window.matchMedia('(min-width: 768px)');
      function size() {
        /* rem (2026-09-30): the same size today (1rem = 10px), and it follows the large-screen root scale */
        svg.style.setProperty('width', ((mq.matches ? wide : narrow) / 10) + 'rem', 'important');
        svg.style.setProperty('height', 'auto', 'important');
      }
      size();
      (mq.addEventListener ? mq.addEventListener.bind(mq, 'change') : mq.addListener.bind(mq))(size);

      /* Replace the <img>, never the <picture> around it. The theme shows one
         of two logos with rules written against the picture element -
         `.hdt-logo picture.hdt-logo-sticky { display: none }` and its sticky
         counterpart - so removing the picture made both copies visible at once
         (the header rendered "CYBEARTCYBEART"). Swapping only the image keeps
         those rules working, and the theme's `img { width: … !important }` no
         longer applies to the mark. */
      var link = img.closest('a') || img.parentNode;
      var picture = img.closest('picture');
      img.parentNode.replaceChild(svg, img);
      if (picture) picture.querySelectorAll('source').forEach(function (n) { n.remove(); });
      wireSweep(svg, link);
    }).catch(function () { /* leave the original <img> in place */ });
  }

  function init() {
    /* phone speed mode (owner 2026-10-24 "make them off on mobile"): the logo stays the plain image on touch screens, same look, no sweep */
    if (document.documentElement.classList.contains('cy-lite')) return false;
    var imgs = document.querySelectorAll('.hdt-main-header .hdt-logo img');
    if (!imgs.length) return false;
    imgs.forEach(inline);
    return true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
